import React, { useState, useRef, useEffect } from 'react';
import { FaCamera, FaImage, FaTrash, FaCircle } from 'react-icons/fa';

const MaterialEditor = ({ material, onMaterialChange, onTexturesChange }) => {
  const [textures, setTextures] = useState({
    albedo: null,
    normal: null,
    roughness: null,
    metallic: null,
    ao: null,
    emissive: null
  });

  const [materialProps, setMaterialProps] = useState({
    color: material?.color || '#ffffff',
    metalness: material?.metalness ?? 0.5,
    roughness: material?.roughness ?? 0.5,
    clearcoat: material?.clearcoat ?? 0,
    clearcoatRoughness: material?.clearcoatRoughness ?? 0,
    reflectivity: material?.reflectivity ?? 0.5,
    transmission: material?.transmission ?? 0,
    emissive: material?.emissive || '#000000',
    emissiveIntensity: material?.emissiveIntensity ?? 0
  });

  const fileInputRefs = {
    albedo: useRef(null),
    normal: useRef(null),
    roughness: useRef(null),
    metallic: useRef(null),
    ao: useRef(null),
    emissive: useRef(null)
  };

  useEffect(() => {
    if (material) {
      setMaterialProps({
        color: material.color || '#ffffff',
        metalness: material.metalness ?? 0.5,
        roughness: material.roughness ?? 0.5,
        clearcoat: material.clearcoat ?? 0,
        clearcoatRoughness: material.clearcoatRoughness ?? 0,
        reflectivity: material.reflectivity ?? 0.5,
        transmission: material.transmission ?? 0,
        emissive: material.emissive || '#000000',
        emissiveIntensity: material.emissiveIntensity ?? 0
      });
    }
  }, [material]);

  const handleTextureUpload = (type, file) => {
    if (!file) return;
    
    const url = URL.createObjectURL(file);
    const newTextures = { ...textures, [type]: url };
    setTextures(newTextures);
    if (onTexturesChange) {
      onTexturesChange(newTextures);
    }
  };

  const handlePropertyChange = (property, value) => {
    const newProps = { ...materialProps, [property]: value };
    setMaterialProps(newProps);
    if (onMaterialChange) {
      onMaterialChange(newProps);
    }
  };

  const removeTexture = (type) => {
    if (textures[type]) {
      URL.revokeObjectURL(textures[type]);
    }
    const newTextures = { ...textures, [type]: null };
    setTextures(newTextures);
    if (onTexturesChange) {
      onTexturesChange(newTextures);
    }
  };

  const SliderControl = ({ label, value, min, max, step, onChange, hint, icon }) => {
    const percentage = ((value - min) / (max - min)) * 100;
    
    return (
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <label className="text-xs font-medium text-dark-text-secondary flex items-center gap-1.5">
            {icon && <span className="text-dark-text-muted">{icon}</span>}
            {label}
          </label>
          <span className="text-xs font-semibold text-dark-text tabular-nums min-w-[3rem] text-right">
            {value.toFixed(2)}
          </span>
        </div>
        <div className="relative">
          <input
            type="range"
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={(e) => onChange(parseFloat(e.target.value))}
            className="w-full h-1.5 bg-dark-border rounded-full appearance-none cursor-pointer slider-thumb"
            style={{
              background: `linear-gradient(to right, #007aff 0%, #007aff ${percentage}%, #e5e5e7 ${percentage}%, #e5e5e7 100%)`
            }}
          />
        </div>
        {hint && (
          <div className="flex justify-between text-[10px] text-dark-text-muted">
            <span>{hint.min}</span>
            <span>{hint.max}</span>
          </div>
        )}
      </div>
    );
  };

  const ColorControl = ({ label, value, onChange, icon }) => (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-dark-text-secondary flex items-center gap-1.5">
        {icon && <span className="text-dark-text-muted">{icon}</span>}
        {label}
      </label>
      <div className="flex items-center gap-2">
        <div className="relative">
          <input
            type="color"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-12 h-10 cursor-pointer rounded-lg border border-dark-border transition-all hover:border-dark-text-muted"
          />
          <div 
            className="absolute inset-0 rounded-lg pointer-events-none border border-dark-border/50"
            style={{ backgroundColor: value }}
          />
        </div>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 py-2 px-3 bg-white border border-dark-border rounded-lg text-xs text-dark-text font-mono transition-all focus:outline-none focus:ring-1 focus:ring-dark-text focus:ring-offset-1"
          placeholder="#ffffff"
        />
      </div>
    </div>
  );

  const TextureSlot = ({ type, label, description, icon }) => (
    <div className="group">
      <label className="block text-xs font-medium text-dark-text-secondary mb-1.5">
        {icon && <span className="inline-block mr-1.5 text-dark-text-muted">{icon}</span>}
        {label}
      </label>
      {textures[type] ? (
        <div className="relative aspect-square rounded-lg border-2 border-dark-border overflow-hidden bg-dark-hover">
          <img 
            src={textures[type]} 
            alt={label} 
            className="w-full h-full object-cover"
          />
          <button
            className="absolute top-1.5 right-1.5 w-6 h-6 bg-white/90 hover:bg-white border border-dark-border rounded-full flex items-center justify-center text-xs text-dark-text transition-all opacity-0 group-hover:opacity-100"
            onClick={() => removeTexture(type)}
            title="Entfernen"
          >
            <FaTrash className="text-[10px]" />
          </button>
          {description && (
            <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[9px] px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {description}
            </div>
          )}
        </div>
      ) : (
        <div
          className="aspect-square rounded-lg border-2 border-dashed border-dark-border bg-white hover:border-dark-text-muted hover:bg-dark-hover transition-all cursor-pointer flex flex-col items-center justify-center group"
          onClick={() => fileInputRefs[type].current?.click()}
        >
          <div className="w-8 h-8 rounded-full bg-dark-border flex items-center justify-center mb-2 group-hover:bg-dark-text-muted transition-colors">
            <FaImage className="text-dark-text-muted text-sm" />
          </div>
          <span className="text-[10px] text-dark-text-muted font-medium">Hochladen</span>
          {description && (
            <span className="text-[9px] text-dark-text-muted mt-0.5 text-center px-2">{description}</span>
          )}
        </div>
      )}
      <input
        ref={fileInputRefs[type]}
        type="file"
        accept="image/*"
        onChange={(e) => handleTextureUpload(type, e.target.files[0])}
        style={{ display: 'none' }}
      />
    </div>
  );

  return (
    <div className="w-full space-y-5">
      {/* Base Properties */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 pb-2 border-b border-dark-border">
          <h3 className="text-xs font-semibold text-dark-text-secondary uppercase tracking-wider">Basis</h3>
        </div>
        
        <ColorControl
          label="Base Color"
          value={materialProps.color}
          onChange={(value) => handlePropertyChange('color', value)}
          icon={<FaCircle className="text-[10px]" />}
        />

        <SliderControl
          label="Metalness"
          value={materialProps.metalness}
          min={0}
          max={1}
          step={0.01}
          onChange={(value) => handlePropertyChange('metalness', value)}
          hint={{ min: 'Dielektrikum', max: 'Metall' }}
        />

        <SliderControl
          label="Roughness"
          value={materialProps.roughness}
          min={0}
          max={1}
          step={0.01}
          onChange={(value) => handlePropertyChange('roughness', value)}
          hint={{ min: 'Glänzend', max: 'Matt' }}
        />
      </div>

      {/* Advanced Properties */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 pb-2 border-b border-dark-border">
          <h3 className="text-xs font-semibold text-dark-text-secondary uppercase tracking-wider">Erweitert</h3>
        </div>

        <SliderControl
          label="Clearcoat"
          value={materialProps.clearcoat}
          min={0}
          max={1}
          step={0.01}
          onChange={(value) => handlePropertyChange('clearcoat', value)}
        />

        <SliderControl
          label="Clearcoat Roughness"
          value={materialProps.clearcoatRoughness}
          min={0}
          max={1}
          step={0.01}
          onChange={(value) => handlePropertyChange('clearcoatRoughness', value)}
        />

        <SliderControl
          label="Reflectivity"
          value={materialProps.reflectivity}
          min={0}
          max={1}
          step={0.01}
          onChange={(value) => handlePropertyChange('reflectivity', value)}
        />

        <SliderControl
          label="Transmission"
          value={materialProps.transmission}
          min={0}
          max={1}
          step={0.01}
          onChange={(value) => handlePropertyChange('transmission', value)}
          hint={{ min: 'Undurchsichtig', max: 'Glas' }}
        />
      </div>

      {/* Emissive */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 pb-2 border-b border-dark-border">
          <h3 className="text-xs font-semibold text-dark-text-secondary uppercase tracking-wider">Emissive</h3>
        </div>

        <ColorControl
          label="Emissive Color"
          value={materialProps.emissive}
          onChange={(value) => handlePropertyChange('emissive', value)}
        />

        <SliderControl
          label="Emissive Intensity"
          value={materialProps.emissiveIntensity}
          min={0}
          max={5}
          step={0.1}
          onChange={(value) => handlePropertyChange('emissiveIntensity', value)}
        />
      </div>

      {/* Textures */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 pb-2 border-b border-dark-border">
          <h3 className="text-xs font-semibold text-dark-text-secondary uppercase tracking-wider">Texturen</h3>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <TextureSlot
            type="albedo"
            label="Albedo"
            description="Hauptfarbe"
            icon={<FaCircle className="text-[10px]" />}
          />
          <TextureSlot
            type="normal"
            label="Normal"
            description="Oberflächendetails"
          />
          <TextureSlot
            type="roughness"
            label="Roughness"
            description="Rauheit (Grau)"
          />
          <TextureSlot
            type="metallic"
            label="Metallic"
            description="Metall (Grau)"
          />
          <TextureSlot
            type="ao"
            label="AO"
            description="Schatten"
          />
          <TextureSlot
            type="emissive"
            label="Emissive"
            description="Selbstleuchtend"
          />
        </div>
      </div>
    </div>
  );
};

export default MaterialEditor;
